import { Action, getModule, Module, Mutation, VuexModule } from "vuex-module-decorators";

import config from "@/config";
import { PROJECT_ANALYTICS_EVENTS, projectAnalytics } from "@/helpers/analytics";
import log from "@/helpers/log";
import { AUTH0_VERIFIER, VERIFIER_MODULE_KEY } from "@/utils/enums";
import { AggregateSubVerifier, UpdateVerifierParams, Verifier } from "@/utils/interface";
import { getSubVerifierType, getVerifierParams } from "@/utils/utils";
import store from "@/vuexStore";

import { authDelete, authGet, authPatch, authPost } from "../authStoreRequest";

// @ts-expect-error decorator is picking the wrong overload as its type
@Module({ namespaced: true, name: VERIFIER_MODULE_KEY, store, dynamic: true })
class VerifierModule extends VuexModule {
  projectVerifiers: Record<string, Array<Verifier>> = {};

  teamLegacyVerifiers: Record<string, Array<Verifier>> = {};

  public get projectVerifiersGetter() {
    return (projectId: string) => {
      return this.projectVerifiers[projectId];
    };
  }

  public get legacyVerifiersGetter() {
    return (teamId: number) => {
      return this.teamLegacyVerifiers[teamId];
    };
  }

  public get legacyVerifierGetter() {
    return (teamId: number, verifierId: string, network: string) => {
      const verifiers = this.teamLegacyVerifiers[teamId] || [];
      return verifiers.find((verifier: Verifier) => verifier.verifier_id === verifierId && verifier.network === network) || ({} as Verifier);
    };
  }

  @Mutation
  public setProjectVerifiers({ projectId, verifiers }: { projectId: string; verifiers: Array<Verifier> }) {
    this.projectVerifiers[projectId] = verifiers;
  }

  @Mutation
  public setTeamLegacyVerifiers({ teamId, verifiers }: { teamId: number; verifiers: Array<Verifier> }) {
    this.teamLegacyVerifiers[teamId] = verifiers;
  }

  @Action
  async getLegacyVerifiers({ teamId }: { teamId: number }) {
    const query: Record<string, string> = { team_id: teamId.toString() };

    const response = await authGet(`${config.backendURL}/verifiers/legacy`, query, {});
    const { verifiers } = response as { verifiers: Array<Verifier> };

    this.setTeamLegacyVerifiers({ teamId, verifiers });
  }

  @Action
  async getProjectVerifiers({ teamId, projectId }: { teamId: number; projectId: string }) {
    const query: Record<string, string> = { include_deleted: "true", team_id: teamId.toString() };
    const response = await authGet(`${config.backendURL}/projects/${projectId}/verifiers`, query, {});
    const { verifiers } = response as { verifiers: Array<Verifier> };
    this.setProjectVerifiers({ projectId, verifiers });
  }

  @Action
  async getVerifierDetails({ projectId, teamId, verifierId }: { verifierId: string; projectId: string; teamId: number }): Promise<Verifier> {
    try {
      const queryParams: Record<string, string> = { team_id: teamId.toString() };

      const response = await authGet(`${config.backendURL}/projects/${projectId}/verifiers/${encodeURIComponent(verifierId)}`, queryParams, {});
      const { verifier } = response as { verifier: Verifier };
      return verifier;
    } catch (error) {
      log.error("Error getting verifier details", error);
      return {} as Verifier;
    }
  }

  @Action
  async createVerifier({
    verifier_id,
    verifier_type,
    network,
    client_id,
    client_secret,
    domain,
    jwt_validation_fields,
    jwt_validation_values,
    jwt_verifier_id,
    jwk_endpoint,
    jwt_verifier_id_case_insensitive,
    team_id,
    project_id,
  }: {
    verifier_id: string;
    verifier_type: string;
    network: string;
    client_id: string;
    client_secret: string;
    domain: string;
    jwt_validation_fields: Array<string>;
    jwt_validation_values: Array<string | boolean>;
    jwt_verifier_id: string;
    jwk_endpoint: string;
    jwt_verifier_id_case_insensitive?: boolean;
    team_id: number;
    project_id: string;
  }): Promise<void> {
    const params = getVerifierParams(
      verifier_type,
      client_id,
      client_secret,
      domain,
      jwt_validation_fields,
      jwt_validation_values,
      jwt_verifier_id,
      jwk_endpoint,
      jwt_verifier_id_case_insensitive
    );

    const requestParameters = {
      verifier_id,
      verifier_type,
      network,
      params: JSON.stringify(params),
      client_id,
      client_secret,
      domain,
      jwt_validation_fields: jwt_validation_fields.toString(),
      jwt_validation_values: jwt_validation_values.toString(),
      jwt_verifier_id,
      jwk_endpoint,
      jwt_verifier_id_case_insensitive,
      team_id,
      project_id,
    };

    try {
      await authPost(`${config.backendURL}/projects/${project_id}/verifiers`, requestParameters, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_CONNECTION_ADDED, {
        connection_type: verifier_type,
        connection_id: verifier_id,
        jwt_validation_fields,
        user_id_field: jwt_verifier_id,
        user_id_case_sensitive_enabled: jwt_verifier_id_case_insensitive,
      });

      await this.getProjectVerifiers({ projectId: project_id, teamId: team_id });
    } catch (error) {
      log.error("Error creating verifier", error);
      throw error;
    }
  }

  @Action
  async createAggregateVerifier({
    verifier_id,
    verifier_type,
    network,
    aggregateSubVerifiers,
    threshold,
    team_id,
    project_id,
  }: {
    verifier_id: string;
    verifier_type: string;
    network: string;
    aggregateSubVerifiers: Array<AggregateSubVerifier>;
    threshold: number;
    team_id: number;
    project_id: string;
  }) {
    const subVerifierDetails = aggregateSubVerifiers.map((subVerifier) => ({
      type_of_verifier: getSubVerifierType(subVerifier.verifier_type),
      verifier_identifier: subVerifier.verifier_id,
      verifier_params: getVerifierParams(
        subVerifier.verifier_type,
        subVerifier.client_id,
        subVerifier.client_secret,
        subVerifier.domain,
        subVerifier.jwt_validation_fields,
        subVerifier.jwt_validation_values,
        subVerifier.jwt_verifier_id,
        subVerifier.jwk_endpoint,
        subVerifier.jwt_verifier_id_case_insensitive
      ),
    }));
    const params = JSON.stringify({
      sub_verifier_details: subVerifierDetails,
      threshold,
    });

    const requestParameters = {
      verifier_id,
      verifier_type,
      network,
      params,
      aggregate_verifier_params: JSON.stringify(aggregateSubVerifiers),
      team_id,
      project_id,
    };
    try {
      await authPost(`${config.backendURL}/projects/${project_id}/verifiers`, requestParameters, {});

      // track analytics
      projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_CONNECTION_ADDED, {
        connection_type: verifier_type,
        connection_id: verifier_id,
        sub_connection_types: aggregateSubVerifiers.map((subVerifier) => subVerifier.verifier_type),
        sub_connections_count: aggregateSubVerifiers.length,
      });

      await this.getProjectVerifiers({ projectId: project_id, teamId: team_id });
    } catch (error) {
      log.error("Error creating verifier", error);
      throw error;
    }
  }

  @Action
  async updateVerifier({
    verifier_id,
    verifier_type,
    client_id,
    client_secret,
    domain,
    jwt_validation_fields,
    jwt_validation_values,
    jwt_verifier_id,
    jwk_endpoint,
    jwt_verifier_id_case_insensitive,
    network,
    team_id,
    project_id,
  }: {
    verifier_id: string;
    verifier_type: string;
    network: string;
    client_id: string;
    client_secret: string;
    domain: string;
    jwt_validation_fields: Array<string>;
    jwt_validation_values: Array<string | boolean>;
    jwt_verifier_id: string;
    jwk_endpoint: string;
    jwt_verifier_id_case_insensitive?: boolean;
    team_id: number;
    project_id: string | undefined;
  }) {
    const params = getVerifierParams(
      verifier_type,
      client_id,
      client_secret,
      domain,
      jwt_validation_fields,
      jwt_validation_values,
      jwt_verifier_id,
      jwk_endpoint,
      jwt_verifier_id_case_insensitive
    );
    // override default value of verifierId for Auth0 verifier
    if (verifier_type === AUTH0_VERIFIER) {
      params.jwt_verifier_id_field = jwt_verifier_id;
    }
    const requestParameters: UpdateVerifierParams = {
      verifier_id,
      client_id,
      client_secret,
      domain,
      jwt_validation_fields: jwt_validation_fields.toString(),
      jwt_validation_values: jwt_validation_values.toString(),
      jwt_verifier_id,
      jwk_endpoint,
      jwt_verifier_id_case_insensitive,
      params: JSON.stringify(params),
      network,
      team_id,
    };

    await this.sendUpdateVerifierRequest({ verifier_id, requestParameters, team_id, project_id });

    // track analytics
    projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_CONNECTION_UPDATED, {
      connection_type: verifier_type,
      connection_id: verifier_id,
      jwt_validation_fields,
      user_id_field: jwt_verifier_id,
      user_id_case_sensitive_enabled: jwt_verifier_id_case_insensitive,
    });
  }

  @Action
  async updateAggregateVerifier({
    verifier_id,
    verifier_type,
    network,
    team_id,
    aggregateSubVerifiers,
    threshold,
    project_id,
  }: {
    verifier_id: string;
    verifier_type: string;
    network: string;
    aggregateSubVerifiers: Array<AggregateSubVerifier>;
    threshold: number;
    team_id: number;
    project_id: string | undefined;
  }) {
    const subVerifierDetails = aggregateSubVerifiers.map((subVerifier) => {
      const params = getVerifierParams(
        subVerifier.verifier_type,
        subVerifier.client_id,
        subVerifier.client_secret,
        subVerifier.domain,
        subVerifier.jwt_validation_fields,
        subVerifier.jwt_validation_values,
        subVerifier.jwt_verifier_id,
        subVerifier.jwk_endpoint,
        subVerifier.jwt_verifier_id_case_insensitive
      );
      // override default value of verifierId for Auth0 verifier
      if (subVerifier.verifier_type === AUTH0_VERIFIER) {
        params.jwt_verifier_id_field = subVerifier.jwt_verifier_id;
      }
      return {
        type_of_verifier: getSubVerifierType(subVerifier.verifier_type),
        verifier_identifier: subVerifier.verifier_id,
        verifier_params: params,
      };
    });

    const params = JSON.stringify({
      sub_verifier_details: subVerifierDetails,
      threshold,
    });

    const requestParameters = {
      verifier_id,
      verifier_type,
      network,
      params,
      aggregate_verifier_params: JSON.stringify(aggregateSubVerifiers),
      team_id,
    };

    await this.sendUpdateVerifierRequest({ verifier_id, requestParameters, team_id, project_id });

    // track analytics
    projectAnalytics.track(PROJECT_ANALYTICS_EVENTS.PROJECT_CONNECTION_UPDATED, {
      connection_type: verifier_type,
      connection_id: verifier_id,
      sub_connection_types: aggregateSubVerifiers.map((subVerifier) => subVerifier.verifier_type),
      sub_connections_count: aggregateSubVerifiers.length,
    });
  }

  @Action
  async validateJWKsEndpoint({ url, network }: { url: string; network: string }) {
    try {
      const data = { jwk_endpoint: url, network };
      await authPost(`${config.backendURL}/verifiers/validate/jwk`, data, {});
    } catch (error) {
      log.error("Error validating JWKs endpoint", error);
      throw error;
    }
  }

  @Action
  async archiveProjectVerifier({
    verifierId,
    projectId,
    teamId,
    network,
  }: {
    verifierId: string;
    projectId: string;
    teamId: number;
    network: string;
  }) {
    try {
      const queryParams: Record<string, string> = { network, team_id: teamId.toString() };
      await authDelete(`${config.backendURL}/projects/${projectId}/verifiers/${encodeURIComponent(verifierId)}`, queryParams, {});
      await this.getProjectVerifiers({ projectId, teamId });
    } catch (error) {
      log.error("Error archiving verifier", error);
      throw error;
    }
  }

  @Action
  async restoreProjectVerifier({
    verifierId,
    projectId,
    teamId,
    network,
  }: {
    verifierId: string;
    projectId: string;
    teamId: number;
    network: string;
  }) {
    try {
      const queryParams: Record<string, string | number> = { network, team_id: teamId };
      await authPatch(`${config.backendURL}/projects/${projectId}/verifiers/${encodeURIComponent(verifierId)}/restore`, queryParams, {});
      await this.getProjectVerifiers({ projectId, teamId });
    } catch (error) {
      log.error("Error restoring verifier", error);
      throw error;
    }
  }

  @Action
  async archiveLegacyVerifier({ verifierId, network, teamId }: { verifierId: string; network: string; teamId: number }) {
    try {
      const queryParams: Record<string, string> = { verifier_id: verifierId, network, team_id: teamId.toString() };

      await authDelete(`${config.backendURL}/verifiers/delete`, queryParams, {});
      await this.getLegacyVerifiers({ teamId });
    } catch (error) {
      log.error("Error archiving legacy verifier", error);
      throw error;
    }
  }

  @Action
  async restoreLegacyVerifier({ verifierId, network, teamId }: { verifierId: string; network: string; teamId: number }) {
    try {
      const data: Record<string, string | number> = { verifier_id: verifierId, network, team_id: teamId };

      await authPatch(`${config.backendURL}/verifiers/restore`, data, {});
      await this.getLegacyVerifiers({ teamId });
    } catch (error) {
      log.error("Error restoring verifier", error);
      throw error;
    }
  }

  @Action
  async moveVerifierToProject({
    verifierId,
    network,
    teamId,
    projectIds,
  }: {
    verifierId: string;
    network: string;
    teamId: number;
    projectIds: string[];
  }) {
    try {
      const data: Record<string, string | number | string[]> = { verifier_id: verifierId, network, project_ids: projectIds, team_id: teamId };

      await authPatch(`${config.backendURL}/verifiers/move-to-project`, data, {});
      await this.getLegacyVerifiers({ teamId });
    } catch (error) {
      log.error("Error moving verifier to projects", error);
      throw error;
    }
  }

  @Action
  async sendUpdateVerifierRequest({
    verifier_id,
    requestParameters,
    team_id,
    project_id,
  }: {
    verifier_id: string;
    requestParameters: UpdateVerifierParams;
    team_id: number;
    project_id?: string;
  }) {
    if (project_id) {
      await authPatch(`${config.backendURL}/projects/${project_id}/verifiers/${encodeURIComponent(verifier_id)}`, requestParameters, {});
      await this.getProjectVerifiers({ projectId: project_id, teamId: team_id });
    } else {
      await authPatch(`${config.backendURL}/verifiers/update`, requestParameters, {});
      await this.getLegacyVerifiers({ teamId: team_id });
    }
  }
}

const verifierModule = getModule(VerifierModule);

export default verifierModule;
